/**
 * Module dependencies.
 */

import { Trade } from 'app/hooks/requests/apps-crypto/use-get-trades';
import React, { useEffect, useRef } from 'react';
import { HeaderForm } from './trade-item.style';
import { useForm } from 'react-hook-form';
import { FormInputField } from 'app/components/organisms/crud/form-components/form-input-field';
import { FormDatePickerField } from 'app/components/organisms/crud/form-components/form-date-picker-field';
import { FormInputNumber } from 'app/components/organisms/crud/form-components/form-input-number';
import { pick } from 'lodash';
import { useEditTrade } from 'app/hooks/requests/apps-crypto/use-edit-trade';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from 'app/components/ui/alert-dialog';
import { Button } from 'app/components/atoms/button/button';
import { Trash2 } from 'lucide-react';
import { useDeleteTrade } from 'app/hooks/requests/apps-crypto/use-delete-trade';

/**
 * `Props` type.
 */

type Props = {
  item: Trade;
};

/**
 * Export `TradeHeader` component.
 */

export function TradeHeader({ item }: Props): JSX.Element {
  const { mutateAsync: editTrade } = useEditTrade();
  const { mutateAsync: deleteTrade } = useDeleteTrade();
  const oldData = useRef<any>();
  const form = useForm({
    values: {
      ...pick(item, ['name', 'dateOfCreation', 'objectivePercentage'])
    },
    mode: 'all'
  });

  const values = form.watch();

  useEffect(() => {
    if (oldData.current === undefined) {
      oldData.current = JSON.stringify(values);

      return;
    }

    const key = JSON.stringify(values);

    if (oldData.current === key) {
      return;
    }

    oldData.current = key;

    const timer = setTimeout(() => {
      editTrade({
        id: item.id,
        payload: {
          ...values,
          objectivePercentage: values.objectivePercentage !== null ? String(values.objectivePercentage) : ''
        }
      });
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [editTrade, item.id, values]);

  return (
    <HeaderForm>
      <FormInputField
        form={form as any}
        item={{
          name: 'name',
          label: 'Trade Name',
          type: 'inputField',
          style: {
            width: '480px'
          },
          rules: undefined
        }}
        name={'name'}
      />

      <FormDatePickerField
        form={form as any}
        item={{
          style: {
            width: '200px'
          },
          name: 'dateOfCreation',
          label: 'Date Of creation',
          type: 'inputField',
          rules: undefined
        }}
        name={'dateOfCreation'}
      />

      <FormInputNumber
        form={form as any}
        item={{
          name: 'objectivePercentage',
          label: 'Objective Percentage',
          formatter: item => item,
          parser: item => item,
          type: 'inputNumberField',
          rules: undefined
        }}
        name={'objectivePercentage'}
      />

      <AlertDialog>
        <AlertDialogTrigger asChild onClick={(event: any) => event.stopPropagation()}>
          <Button danger icon={<Trash2 className={'h-4 w-4'} />} size={'small'} />
        </AlertDialogTrigger>

        <AlertDialogContent onClick={(event: any) => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{'Are you sure?'}</AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
              onClick={(event: any) => {
                event.stopPropagation();
                deleteTrade(item.id);
              }}
            >
              {'OK'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HeaderForm>
  );
}
